import type { StudioBeautyMasks } from './studioImageBeautyMasks'

export type WeatherEffectType = 'none' | 'rain' | 'snow' | 'stars' | 'fog' | 'lightning' | 'sunrays' | 'aurora'

export interface StudioLookRecipe {
  exposure: number
  contrast: number
  saturation: number
  vibrance: number
  temperature: number
  tint: number
  fade: number
  shadow: [number, number, number]
  highlight: [number, number, number]
  grain: number
  vignette: number
  weather?: WeatherEffectType
  weatherIntensity?: number
}

export type StudioFilterCategory = 'retro' | 'analog' | 'sinematik' | 'modern' | 'siyah-beyaz' | 'hava' | 'mevsimler'

export interface StudioFilterCategoryMeta {
  id: StudioFilterCategory
  label: string
  englishLabel: string
}

export const STUDIO_FILTER_CATEGORIES: StudioFilterCategoryMeta[] = [
  { id: 'retro', label: 'Retro', englishLabel: 'Retro' },
  { id: 'analog', label: 'Analog', englishLabel: 'Analog' },
  { id: 'sinematik', label: 'Sinematik', englishLabel: 'Cinematic' },
  { id: 'modern', label: 'Modern', englishLabel: 'Modern' },
  { id: 'siyah-beyaz', label: 'Siyah Beyaz', englishLabel: 'Black & White' },
  { id: 'hava', label: 'Hava', englishLabel: 'Weather' },
  { id: 'mevsimler', label: 'Mevsimler', englishLabel: 'Seasons' },
]

export interface StudioLook {
  id: string
  label: string
  englishLabel: string
  recipe: StudioLookRecipe
  category?: StudioFilterCategory
}

export interface StudioBeautyAdjustments {
  skinSmooth: number
  skinTexture: number
  skinTone: number
  skinRedness: number
  skinVitality: number
  blemish: number
  underEye: number
  eyeBrightness: number
  eyeWhitening: number
  eyeClarity: number
  eyeVitality: number
  teethWhitening: number
  lipVitality: number
  lipBrightness: number
  faceLight: number
  faceShadows: number
  faceHighlights: number
  faceTemperature: number
  shineReduction: number
  glow: number
  glowSpread: number
}

export const EMPTY_STUDIO_BEAUTY_ADJUSTMENTS: StudioBeautyAdjustments = {
  skinSmooth: 0,
  skinTexture: 0,
  skinTone: 0,
  skinRedness: 0,
  skinVitality: 0,
  blemish: 0,
  underEye: 0,
  eyeBrightness: 0,
  eyeWhitening: 0,
  eyeClarity: 0,
  eyeVitality: 0,
  teethWhitening: 0,
  lipVitality: 0,
  lipBrightness: 0,
  faceLight: 0,
  faceShadows: 0,
  faceHighlights: 0,
  faceTemperature: 0,
  shineReduction: 0,
  glow: 0,
  glowSpread: 0,
}

const look = (
  id: string,
  label: string,
  englishLabel: string,
  values: Partial<StudioLookRecipe> = {},
  category?: StudioFilterCategory,
): StudioLook => ({
  id,
  label,
  englishLabel,
  category,
  recipe: {
    exposure: 0,
    contrast: 0,
    saturation: 0,
    vibrance: 0,
    temperature: 0,
    tint: 0,
    fade: 0,
    shadow: [0, 0, 0],
    highlight: [0, 0, 0],
    grain: 0,
    vignette: 0,
    weather: 'none',
    weatherIntensity: 1,
    ...values,
  },
})

export const STUDIO_LOOKS: StudioLook[] = [
  look('original', 'Orijinal', 'Original'),

  // ═══════════════════════════════════════════════════════════
  // RETRO (15)
  // ═══════════════════════════════════════════════════════════
  look('retro-polaroid', 'Polaroid', 'Polaroid', { exposure: .04, contrast: -.16, saturation: -.1, temperature: .1, fade: .17, grain: .1, vignette: .1 }, 'retro'),
  look('retro-kodachrome', 'Kodachrome', 'Kodachrome', { contrast: .1, saturation: .18, vibrance: .12, temperature: .08, shadow: [.02, .01, -.03], highlight: [.04, .02, -.01], grain: .08 }, 'retro'),
  look('retro-agfa-vista', 'Agfa Vista', 'Agfa Vista', { exposure: .03, contrast: .06, saturation: .22, vibrance: .15, temperature: .06, tint: -.02, shadow: [.015, .008, -.02], grain: .12 }, 'retro'),
  look('retro-ektachrome', 'Ektachrome', 'Ektachrome', { contrast: .08, saturation: .1, vibrance: .16, temperature: -.04, tint: .02, shadow: [-.01, .015, .035], highlight: [.02, .01, .04], grain: .06 }, 'retro'),
  look('retro-technicolor', 'Technicolor', 'Technicolor', { contrast: .15, saturation: .28, vibrance: .2, shadow: [.03, -.01, -.02], highlight: [.05, .02, -.02], grain: .04, vignette: .06 }, 'retro'),
  look('retro-1970-warm', '70\'ler Sıcak', '70s Warm', { exposure: .05, contrast: -.05, saturation: .08, temperature: .18, tint: .03, fade: .06, shadow: [.04, .025, -.01], grain: .14 }, 'retro'),
  look('retro-1980-neon', '80\'ler Neon', '80s Neon', { contrast: .16, saturation: .24, vibrance: .18, temperature: -.03, shadow: [.04, -.02, .06], highlight: [.06, -.01, .05], grain: .06 }, 'retro'),
  look('retro-1990-grunge', '90\'lar Grunge', '90s Grunge', { exposure: -.04, contrast: .2, saturation: -.12, temperature: .04, fade: .04, grain: .2, vignette: .16 }, 'retro'),
  look('retro-instant-warm', 'Anı Sıcak', 'Instant Warm', { exposure: .06, contrast: -.08, saturation: .06, temperature: .14, tint: .02, fade: .08, highlight: [.04, .03, .01], grain: .1 }, 'retro'),
  look('retro-faded-glory', 'Soluk Zafer', 'Faded Glory', { exposure: .03, contrast: -.1, saturation: -.06, fade: .14, temperature: .06, grain: .08 }, 'retro'),
  look('retro-velvia-pop', 'Velvia Pop', 'Velvia Pop', { contrast: .12, saturation: .26, vibrance: .22, shadow: [-.01, .01, .02], highlight: [.03, .015, -.01], grain: .04 }, 'retro'),
  look('retro-superia-400', 'Superia 400', 'Superia 400', { exposure: .02, contrast: .04, saturation: .06, temperature: .05, tint: -.015, shadow: [.01, .012, .025], grain: .14 }, 'retro'),
  look('retro-disposable', 'Tek Kullanımlık', 'Disposable', { exposure: .04, contrast: .03, saturation: -.03, temperature: .1, tint: -.035, fade: .06, grain: .24, vignette: .14 }, 'retro'),
  look('retro-dusk-memory', 'Akşam Anısı', 'Dusk Memory', { exposure: .02, contrast: -.06, saturation: .04, temperature: .12, tint: .04, fade: .1, shadow: [.03, .02, .005], grain: .09 }, 'retro'),
  look('retro-nostalgia', 'Nostalji', 'Nostalgia', { exposure: .03, contrast: -.04, saturation: -.04, temperature: .1, fade: .12, shadow: [.025, .015, -.005], highlight: [.03, .02, .01], grain: .11, vignette: .08 }, 'retro'),

  // ═══════════════════════════════════════════════════════════
  // ANALOG (15)
  // ═══════════════════════════════════════════════════════════
  look('analog-35mm-gold', '35mm Gold', '35mm Gold', { contrast: -.035, saturation: .1, temperature: .12, shadow: [-.02, .015, .045], highlight: [.035, .018, -.01], grain: .16 }, 'analog'),
  look('analog-portra-160', 'Portra 160', 'Portra 160', { exposure: .04, contrast: -.09, saturation: .035, vibrance: .08, temperature: .07, fade: .035, grain: .09 }, 'analog'),
  look('analog-portra-400', 'Portra 400', 'Portra 400', { exposure: .035, contrast: -.06, saturation: .05, vibrance: .1, temperature: .09, tint: .01, fade: .04, shadow: [.01, .008, -.005], grain: .12 }, 'analog'),
  look('analog-portra-800', 'Portra 800', 'Portra 800', { exposure: .05, contrast: -.04, saturation: .04, vibrance: .06, temperature: .1, tint: .015, fade: .05, grain: .18, vignette: .04 }, 'analog'),
  look('analog-fuji-c200', 'Fuji C200', 'Fuji C200', { contrast: .03, saturation: .08, vibrance: .1, temperature: -.02, tint: .01, shadow: [-.005, .01, .025], grain: .1 }, 'analog'),
  look('analog-fuji-pro400h', 'Fuji Pro 400H', 'Fuji Pro 400H', { exposure: .03, contrast: -.06, saturation: -.02, vibrance: .12, temperature: -.04, tint: .015, shadow: [-.01, .02, .04], highlight: [.02, .015, .005], grain: .1 }, 'analog'),
  look('analog-fuji-superia', 'Fuji Superia', 'Fuji Superia', { contrast: .045, saturation: -.015, temperature: -.075, tint: .02, shadow: [-.01, .02, .05], highlight: [.025, .018, -.005], grain: .12 }, 'analog'),
  look('analog-cinestill-800t', 'CineStill 800T', 'CineStill 800T', { exposure: .02, contrast: .08, saturation: .06, temperature: -.08, tint: .01, shadow: [-.01, .01, .04], highlight: [.06, .02, .07], grain: .14, vignette: .06 }, 'analog'),
  look('analog-ilford-hp5', 'Ilford HP5', 'Ilford HP5', { contrast: .18, saturation: -1, grain: .16, vignette: .06 }, 'analog'),
  look('analog-tri-x-400', 'Tri-X 400', 'Tri-X 400', { contrast: .22, saturation: -1, shadow: [.01, .01, .01], grain: .2, vignette: .08 }, 'analog'),
  look('analog-lomo-purple', 'Lomo Purple', 'Lomo Purple', { contrast: .14, saturation: .18, temperature: -.02, tint: .06, shadow: [.03, -.01, .05], highlight: [.04, .01, .06], grain: .12, vignette: .14 }, 'analog'),
  look('analog-cross-process', 'Cross Process', 'Cross Process', { contrast: .2, saturation: .14, tint: -.035, shadow: [-.015, .045, .015], highlight: [.07, .025, -.045], grain: .09 }, 'analog'),
  look('analog-expired-roll', 'Expired Film', 'Expired Film', { exposure: .04, contrast: -.06, saturation: -.08, temperature: .08, tint: .03, fade: .1, shadow: [.025, .02, .005], grain: .22, vignette: .1 }, 'analog'),
  look('analog-push-process', 'Push Process', 'Push Process', { exposure: .08, contrast: .2, saturation: -.04, grain: .22, vignette: .08 }, 'analog'),
  look('analog-double-exposure', 'Çift Pozlama', 'Double Exposure', { exposure: .1, contrast: -.08, saturation: .04, vibrance: .08, fade: .06, highlight: [.04, .03, .02], grain: .08 }, 'analog'),

  // ═══════════════════════════════════════════════════════════
  // SİNEMATİK (15)
  // ═══════════════════════════════════════════════════════════
  look('cine-teal-orange', 'Teal & Orange', 'Teal & Orange', { contrast: .14, saturation: -.055, shadow: [-.025, .045, .075], highlight: [.065, .028, -.015], vignette: .12 }, 'sinematik'),
  look('cine-blockbuster', 'Blockbuster', 'Blockbuster', { exposure: -.025, contrast: .24, saturation: .045, vibrance: .12, shadow: [-.045, .035, .08], highlight: [.09, .025, -.035], vignette: .16 }, 'sinematik'),
  look('cine-noir', 'Film Noir', 'Film Noir', { exposure: -.06, contrast: .28, saturation: -1, shadow: [.01, .01, .01], grain: .1, vignette: .22 }, 'sinematik'),
  look('cine-bleach-bypass', 'Bleach Bypass', 'Bleach Bypass', { exposure: -.035, contrast: .34, saturation: -.48, grain: .14, vignette: .08 }, 'sinematik'),
  look('cine-wes-anderson', 'Wes Anderson', 'Wes Anderson', { exposure: .04, contrast: -.06, saturation: .16, vibrance: .14, temperature: .06, shadow: [.02, .01, -.01], highlight: [.03, .02, .01], grain: .03 }, 'sinematik'),
  look('cine-deakins', 'Deakins', 'Deakins', { exposure: -.03, contrast: .12, saturation: -.08, temperature: -.03, shadow: [-.015, .02, .04], highlight: [.03, .015, -.01], vignette: .1 }, 'sinematik'),
  look('cine-tarantino', 'Tarantino', 'Tarantino', { contrast: .22, saturation: .12, vibrance: .08, temperature: .05, shadow: [.02, -.01, -.02], highlight: [.04, .02, -.015], grain: .06, vignette: .14 }, 'sinematik'),
  look('cine-kubrick-cold', 'Kubrick Soğuk', 'Kubrick Cold', { exposure: -.04, contrast: .18, saturation: -.1, temperature: -.08, tint: .02, shadow: [-.01, .01, .04], highlight: [.01, .015, .04], vignette: .12 }, 'sinematik'),
  look('cine-anamorphic', 'Anamorfik', 'Anamorphic', { contrast: .1, saturation: .04, vibrance: .06, temperature: .03, shadow: [-.01, .02, .04], highlight: [.05, .025, .06], grain: .04, vignette: .18 }, 'sinematik'),
  look('cine-golden-age', 'Altın Çağ', 'Golden Age', { exposure: .03, contrast: .06, saturation: .06, temperature: .12, shadow: [.02, .01, -.01], highlight: [.04, .025, .005], grain: .06, vignette: .08 }, 'sinematik'),
  look('cine-indie', 'Bağımsız', 'Indie', { exposure: .02, contrast: -.04, saturation: -.06, vibrance: .08, temperature: .04, fade: .06, grain: .1 }, 'sinematik'),
  look('cine-sci-fi', 'Bilim Kurgu', 'Sci-Fi', { exposure: -.03, contrast: .16, saturation: .06, temperature: -.06, tint: .03, shadow: [.02, .01, .06], highlight: [.03, .02, .06], vignette: .14 }, 'sinematik'),
  look('cine-horror-green', 'Korku Yeşil', 'Horror Green', { exposure: -.06, contrast: .2, saturation: -.14, temperature: -.04, tint: -.06, shadow: [-.02, .03, .01], highlight: [-.01, .02, -.01], grain: .08, vignette: .2 }, 'sinematik'),
  look('cine-romance', 'Romantik', 'Romance', { exposure: .04, contrast: -.06, saturation: .06, vibrance: .1, temperature: .08, highlight: [.04, .025, .015], grain: .03, vignette: .06 }, 'sinematik'),
  look('cine-dream-sequence', 'Rüya', 'Dream Sequence', { exposure: .08, contrast: -.12, saturation: .025, vibrance: .1, temperature: .045, fade: .05, highlight: [.055, .04, .025], grain: .025 }, 'sinematik'),

  // ═══════════════════════════════════════════════════════════
  // MODERN (15)
  // ═══════════════════════════════════════════════════════════
  look('mod-clean', 'Temiz', 'Clean', { exposure: .025, contrast: .045, saturation: -.025, vibrance: .08 }, 'modern'),
  look('mod-editorial', 'Editöryal', 'Editorial', { exposure: .035, contrast: -.12, saturation: -.1, temperature: .035, fade: .11, grain: .035 }, 'modern'),
  look('mod-luxury', 'Lüks', 'Luxury', { exposure: -.11, contrast: .26, saturation: -.19, vibrance: -.035, shadow: [-.01, -.005, .015], vignette: .16 }, 'modern'),
  look('mod-y2k', 'Y2K', 'Y2K', { exposure: .075, contrast: .12, saturation: .17, temperature: -.04, tint: .055, grain: .18 }, 'modern'),
  look('mod-cyberpunk', 'Cyberpunk', 'Cyberpunk', { exposure: -.025, contrast: .2, saturation: .27, vibrance: .18, shadow: [.055, -.025, .09], highlight: [.085, -.02, .07], grain: .055, vignette: .15 }, 'modern'),
  look('mod-tokyo-neon', 'Tokyo Neon', 'Tokyo Neon', { contrast: .18, saturation: .22, vibrance: .22, shadow: [.035, -.015, .085], highlight: [.07, -.025, .065], grain: .04, vignette: .13 }, 'modern'),
  look('mod-chrome', 'Chrome', 'Chrome', { exposure: -.025, contrast: .25, saturation: -.16, temperature: -.055, tint: .025, shadow: [-.025, .005, .04], highlight: [.025, .035, .055] }, 'modern'),
  look('mod-moody-green', 'Karamsar Yeşil', 'Moody Green', { exposure: -.14, contrast: .1, saturation: -.11, tint: -.06, shadow: [-.015, .04, .015], highlight: [.025, .025, .005], grain: .1, vignette: .18 }, 'modern'),
  look('mod-peach-glow', 'Şeftali Işıltı', 'Peach Glow', { exposure: .05, contrast: -.04, saturation: .08, vibrance: .1, temperature: .1, tint: .03, highlight: [.04, .025, .01], grain: .02 }, 'modern'),
  look('mod-lavender-haze', 'Lavanta Sis', 'Lavender Haze', { exposure: .03, contrast: -.06, saturation: -.04, vibrance: .06, temperature: -.02, tint: .06, shadow: [.02, .01, .04], highlight: [.03, .02, .05], grain: .03 }, 'modern'),
  look('mod-direct-flash', 'Direct Flash', 'Direct Flash', { exposure: .12, contrast: .2, saturation: .06, temperature: -.04, shadow: [-.025, -.035, -.01], grain: .08 }, 'modern'),
  look('mod-halftone-pop', 'Halftone Pop', 'Halftone Pop', { contrast: .34, saturation: .23, vibrance: .2 }, 'modern'),
  look('mod-minimal', 'Minimal', 'Minimal', { exposure: .02, contrast: .02, saturation: -.12, vibrance: .04, temperature: -.02, grain: .02 }, 'modern'),
  look('mod-warm-fade', 'Sıcak Soluk', 'Warm Fade', { exposure: .04, contrast: -.08, saturation: -.02, temperature: .1, fade: .08, grain: .04 }, 'modern'),
  look('mod-deep-shadow', 'Derin Gölge', 'Deep Shadow', { exposure: -.08, contrast: .18, saturation: .04, shadow: [-.01, .005, .02], vignette: .14 }, 'modern'),

  // ═══════════════════════════════════════════════════════════
  // SİYAH BEYAZ (15)
  // ═══════════════════════════════════════════════════════════
  look('bw-classic', 'Klasik S/B', 'Classic B&W', { contrast: .12, saturation: -1, grain: .06 }, 'siyah-beyaz'),
  look('bw-high-contrast', 'Yüksek Kontrast', 'High Contrast', { contrast: .32, saturation: -1, grain: .06 }, 'siyah-beyaz'),
  look('bw-low-key', 'Low Key', 'Low Key', { exposure: -.1, contrast: .28, saturation: -1, vignette: .18 }, 'siyah-beyaz'),
  look('bw-high-key', 'High Key', 'High Key', { exposure: .1, contrast: -.08, saturation: -1 }, 'siyah-beyaz'),
  look('bw-noir-silver', 'Noir Silver', 'Noir Silver', { contrast: .26, saturation: -1, fade: .025, grain: .14, vignette: .18 }, 'siyah-beyaz'),
  look('bw-soft', 'Yumuşak S/B', 'Soft B&W', { contrast: -.09, saturation: -1, fade: .09, grain: .07 }, 'siyah-beyaz'),
  look('bw-true-sepia', 'Voxhia Sepia', 'Voxhia Sepia', { contrast: .18, saturation: -1, temperature: .24, tint: .04, shadow: [.14, .06, -.04], highlight: [.10, .07, .01], grain: .08, vignette: .06 }, 'siyah-beyaz'),
  look('bw-selenium', 'Selenyum', 'Selenium', { contrast: .16, saturation: -1, temperature: -.06, shadow: [-.02, .005, .04], highlight: [.01, .015, .035], grain: .06 }, 'siyah-beyaz'),
  look('bw-cyanotype', 'Siyanotip', 'Cyanotype', { contrast: .14, saturation: -1, temperature: -.12, tint: -.02, shadow: [-.04, .02, .08], highlight: [-.02, .03, .08], grain: .06 }, 'siyah-beyaz'),
  look('bw-infrared', 'Infrared', 'Infrared', { exposure: .06, contrast: .14, saturation: -1, shadow: [.01, .01, .01], highlight: [.03, .03, .03], grain: .12, vignette: .08 }, 'siyah-beyaz'),
  look('bw-dramatic', 'Dramatik', 'Dramatic', { exposure: -.04, contrast: .36, saturation: -1, grain: .1, vignette: .2 }, 'siyah-beyaz'),
  look('bw-platinum', 'Platin', 'Platinum', { contrast: .08, saturation: -1, fade: .04, shadow: [.02, .02, .02], highlight: [.03, .03, .03], grain: .04 }, 'siyah-beyaz'),
  look('bw-newspaper', 'Gazete', 'Newspaper', { contrast: .3, saturation: -1, fade: .06, grain: .24 }, 'siyah-beyaz'),
  look('bw-split-tone', 'Split Ton', 'Split Tone', { contrast: .14, saturation: -1, shadow: [-.02, .01, .04], highlight: [.06, .035, .005], grain: .06 }, 'siyah-beyaz'),
  look('bw-street', 'Sokak', 'Street', { contrast: .22, saturation: -1, vibrance: -.1, grain: .16, vignette: .12 }, 'siyah-beyaz'),

  // ═══════════════════════════════════════════════════════════
  // HAVA (15)
  // ═══════════════════════════════════════════════════════════
  look('weather-golden-hour', 'Altın Saat', 'Golden Hour', { exposure: .08, contrast: .015, saturation: .09, temperature: .2, highlight: [.09, .045, -.03], grain: .045, weather: 'sunrays', weatherIntensity: 1.0 }, 'hava'),
  look('weather-blue-hour', 'Mavi Saat', 'Blue Hour', { exposure: -.04, contrast: .06, saturation: -.04, temperature: -.12, tint: .03, shadow: [-.02, .01, .05], highlight: [.01, .02, .06], weather: 'stars', weatherIntensity: 0.65 }, 'hava'),
  look('weather-overcast', 'Bulutlu', 'Overcast', { exposure: .02, contrast: -.08, saturation: -.12, temperature: -.04, fade: .04, weather: 'fog', weatherIntensity: 0.5 }, 'hava'),
  look('weather-foggy', 'Sisli', 'Foggy', { exposure: .06, contrast: -.16, saturation: -.14, fade: .12, grain: .03, weather: 'fog', weatherIntensity: 1.1 }, 'hava'),
  look('weather-rainy', 'Yağmurlu', 'Rainy', { exposure: -.03, contrast: .06, saturation: -.08, temperature: -.06, shadow: [-.01, .01, .03], grain: .04, weather: 'rain', weatherIntensity: 1.0 }, 'hava'),
  look('weather-stormy', 'Fırtınalı', 'Stormy', { exposure: -.08, contrast: .18, saturation: -.16, temperature: -.04, shadow: [-.02, .005, .03], grain: .06, vignette: .14, weather: 'rain', weatherIntensity: 1.35 }, 'hava'),
  look('weather-sunset-fire', 'Gün Batımı', 'Sunset Fire', { exposure: .06, contrast: .08, saturation: .14, vibrance: .1, temperature: .22, shadow: [.04, .01, -.03], highlight: [.08, .04, -.02], vignette: .06, weather: 'sunrays', weatherIntensity: 0.8 }, 'hava'),
  look('weather-sunrise-soft', 'Gün Doğumu', 'Sunrise Soft', { exposure: .06, contrast: -.04, saturation: .06, vibrance: .08, temperature: .12, tint: .02, highlight: [.05, .03, .01], grain: .02, weather: 'sunrays', weatherIntensity: 0.85 }, 'hava'),
  look('weather-snow', 'Karlı', 'Snow', { exposure: .08, contrast: -.06, saturation: -.1, temperature: -.06, highlight: [.02, .02, .03], weather: 'snow', weatherIntensity: 1.0 }, 'hava'),
  look('weather-hazy', 'Puslu', 'Hazy', { exposure: .04, contrast: -.1, saturation: -.06, temperature: .04, fade: .08, grain: .02, weather: 'fog', weatherIntensity: 0.75 }, 'hava'),
  look('weather-moonlight', 'Ay Işığı', 'Moonlight', { exposure: -.1, contrast: .1, saturation: -.2, temperature: -.1, tint: .02, shadow: [-.01, .01, .04], vignette: .12, weather: 'stars', weatherIntensity: 1.0 }, 'hava'),
  look('weather-tropical', 'Tropik', 'Tropical', { exposure: .04, contrast: .04, saturation: .16, vibrance: .14, temperature: .08, highlight: [.03, .02, -.01], grain: .02, weather: 'sunrays', weatherIntensity: 0.6 }, 'hava'),
  look('weather-aurora', 'Kuzey Işıkları', 'Aurora', { exposure: -.02, contrast: .08, saturation: .12, vibrance: .16, temperature: -.06, tint: .04, shadow: [.02, .03, .06], highlight: [.03, .04, .06], weather: 'aurora', weatherIntensity: 1.0 }, 'hava'),
  look('weather-dust', 'Tozlu', 'Dust', { exposure: .03, contrast: .04, saturation: -.06, temperature: .1, tint: .02, fade: .04, shadow: [.02, .015, .005], grain: .08, weather: 'fog', weatherIntensity: 0.6 }, 'hava'),
  look('weather-lightning', 'Şimşek', 'Lightning', { exposure: -.06, contrast: .22, saturation: -.08, temperature: -.06, shadow: [-.02, .005, .04], highlight: [.04, .04, .06], vignette: .16, weather: 'lightning', weatherIntensity: 1.0 }, 'hava'),

  // ═══════════════════════════════════════════════════════════
  // MEVSİMLER (15)
  // ═══════════════════════════════════════════════════════════
  look('season-spring-bloom', 'Bahar Çiçeği', 'Spring Bloom', { exposure: .04, contrast: -.02, saturation: .1, vibrance: .14, temperature: .04, tint: .02, highlight: [.03, .02, .01], grain: .02 }, 'mevsimler'),
  look('season-spring-rain', 'Bahar Yağmuru', 'Spring Rain', { exposure: .02, contrast: -.04, saturation: .04, vibrance: .08, temperature: -.02, tint: .01, shadow: [-.005, .01, .02], grain: .03, weather: 'rain', weatherIntensity: 0.8 }, 'mevsimler'),
  look('season-summer-sun', 'Yaz Güneşi', 'Summer Sun', { exposure: .06, contrast: .04, saturation: .12, vibrance: .1, temperature: .14, highlight: [.05, .03, -.01], grain: .03, weather: 'sunrays', weatherIntensity: 0.85 }, 'mevsimler'),
  look('season-summer-beach', 'Sahil', 'Beach', { exposure: .08, contrast: -.02, saturation: .06, vibrance: .12, temperature: .1, tint: -.01, highlight: [.04, .03, .01], grain: .02 }, 'mevsimler'),
  look('season-summer-night', 'Yaz Gecesi', 'Summer Night', { exposure: -.06, contrast: .1, saturation: .04, temperature: -.04, tint: .03, shadow: [.01, .005, .03], vignette: .1, weather: 'stars', weatherIntensity: 1.0 }, 'mevsimler'),
  look('season-autumn-gold', 'Sonbahar Altını', 'Autumn Gold', { exposure: .02, contrast: .06, saturation: .08, vibrance: .06, temperature: .14, tint: .02, shadow: [.02, .01, -.015], highlight: [.04, .025, -.01], grain: .04 }, 'mevsimler'),
  look('season-autumn-harvest', 'Hasat', 'Harvest', { exposure: .01, contrast: .08, saturation: .06, temperature: .12, shadow: [.03, .015, -.02], highlight: [.035, .02, -.01], grain: .06, vignette: .06 }, 'mevsimler'),
  look('season-autumn-fog', 'Sonbahar Sisi', 'Autumn Fog', { exposure: .03, contrast: -.1, saturation: -.04, temperature: .06, fade: .08, grain: .04, weather: 'fog', weatherIntensity: 0.9 }, 'mevsimler'),
  look('season-winter-frost', 'Kış Ayazı', 'Winter Frost', { exposure: .02, contrast: .08, saturation: -.1, temperature: -.1, shadow: [-.01, .005, .03], highlight: [.01, .015, .03], weather: 'snow', weatherIntensity: 0.65 }, 'mevsimler'),
  look('season-winter-cozy', 'Kış Sıcağı', 'Winter Cozy', { exposure: .03, contrast: -.04, saturation: .02, temperature: .12, tint: .02, shadow: [.02, .01, -.005], highlight: [.03, .02, .005], grain: .04, vignette: .06 }, 'mevsimler'),
  look('season-winter-blue', 'Kış Mavisi', 'Winter Blue', { exposure: -.02, contrast: .06, saturation: -.06, temperature: -.1, shadow: [-.015, .01, .04], highlight: [.005, .01, .035] }, 'mevsimler'),
  look('season-cherry-blossom', 'Sakura', 'Cherry Blossom', { exposure: .05, contrast: -.04, saturation: .06, vibrance: .1, temperature: .04, tint: .04, highlight: [.04, .025, .03], grain: .02 }, 'mevsimler'),
  look('season-monsoon', 'Muson', 'Monsoon', { exposure: -.04, contrast: .08, saturation: -.06, temperature: -.04, tint: -.02, shadow: [-.01, .01, .025], grain: .06, vignette: .08, weather: 'rain', weatherIntensity: 1.4 }, 'mevsimler'),
  look('season-indian-summer', 'Pastırma Yazı', 'Indian Summer', { exposure: .04, contrast: .02, saturation: .1, vibrance: .08, temperature: .16, highlight: [.05, .03, -.01], grain: .04 }, 'mevsimler'),
  look('season-first-snow', 'İlk Kar', 'First Snow', { exposure: .06, contrast: -.04, saturation: -.08, temperature: -.06, tint: .01, highlight: [.02, .02, .025], grain: .03, weather: 'snow', weatherIntensity: 0.9 }, 'mevsimler'),
]

const LOOK_ALIASES: Record<string, string> = {
  // Legacy IDs -> new IDs
  'noir-silver': 'bw-noir-silver',
  'bw': 'bw-soft',
  'voxhia': 'bw-true-sepia',
  'halftone-pop': 'mod-halftone-pop',
  'refined-grid': 'bw-newspaper',
  'digicam-2005': 'retro-disposable',
  '35mm-gold': 'analog-35mm-gold',
  'cinematic-teal': 'cine-teal-orange',
  'dream-glow': 'cine-dream-sequence',
  'chrome': 'mod-chrome',
  'tokyo-neon': 'mod-tokyo-neon',
  'direct-flash': 'mod-direct-flash',
  'disposable': 'retro-disposable',
  'fuji-cool': 'analog-fuji-superia',
  'portra-soft': 'analog-portra-160',
  'blockbuster': 'cine-blockbuster',
  'moody-green': 'mod-moody-green',
  'golden-hour': 'weather-golden-hour',
  'editorial-matte': 'mod-editorial',
  'luxury-black': 'mod-luxury',
  'clean-product': 'mod-clean',
  'y2k': 'mod-y2k',
  'polaroid-fade': 'retro-polaroid',
  'bleach-bypass': 'cine-bleach-bypass',
  'infrared-dream': 'bw-infrared',
  'cross-process': 'analog-cross-process',
  'cyberpunk': 'mod-cyberpunk',
  'soft-bw': 'bw-soft',
  'refined-grit': 'bw-newspaper',
}

export interface StudioFilterAdjustments {
  lookId: string
  sharpness: number
  beauty: StudioBeautyAdjustments
  intensity: number
  exposure: number
  contrast: number
  saturation: number
  temperature: number
  tint: number
  highlights: number
  shadows: number
  fade: number
  grain: number
  grainSize: number
  vignette: number
  vignetteFeather: number
}

export const DEFAULT_FILTER_ADJUSTMENTS: StudioFilterAdjustments = {
  lookId: 'original',
  sharpness: 0,
  beauty: EMPTY_STUDIO_BEAUTY_ADJUSTMENTS,
  intensity: 100,
  exposure: 0,
  contrast: 0,
  saturation: 0,
  temperature: 0,
  tint: 0,
  highlights: 0,
  shadows: 0,
  fade: 0,
  grain: 0,
  grainSize: 1,
  vignette: 0,
  vignetteFeather: .6,
}

const LUT_SIZE = 33

const srgbToLinear = (value: number) => value <= .04045
  ? value / 12.92
  : ((value + .055) / 1.055) ** 2.4

const linearToSrgb = (value: number) => {
  const safe = Math.max(0, Math.min(1, value))
  return safe <= .0031308 ? safe * 12.92 : 1.055 * safe ** (1 / 2.4) - .055
}

const smoothstep = (edge0: number, edge1: number, value: number) => {
  const t = Math.max(0, Math.min(1, (value - edge0) / (edge1 - edge0)))
  return t * t * (3 - 2 * t)
}

const makeLut = (recipe: StudioLookRecipe) => {
  const data = new Uint8Array(LUT_SIZE ** 3 * 4)
  let offset = 0
  for (let blue = 0; blue < LUT_SIZE; blue += 1) {
    for (let green = 0; green < LUT_SIZE; green += 1) {
      for (let red = 0; red < LUT_SIZE; red += 1) {
        const srgb: [number, number, number] = [red / (LUT_SIZE - 1), green / (LUT_SIZE - 1), blue / (LUT_SIZE - 1)]
        let color = srgb.map(srgbToLinear) as [number, number, number]
        const luminance = color[0]! * .2126 + color[1]! * .7152 + color[2]! * .0722
        const shadowWeight = 1 - smoothstep(.04, .62, luminance)
        const highlightWeight = smoothstep(.38, .96, luminance)
        color = color.map((channel) => channel * (2 ** recipe.exposure)) as [number, number, number]
        const pivot = .18
        color = color.map((channel) => (channel - pivot) * (1 + recipe.contrast) + pivot) as [number, number, number]
        color = color.map((channel, index) => channel + recipe.shadow[index]! * shadowWeight + recipe.highlight[index]! * highlightWeight) as [number, number, number]
        color[0] = color[0]! + recipe.temperature * .11
        color[2] = color[2]! - recipe.temperature * .11
        color[0] = color[0]! + recipe.tint * .07
        color[1] = color[1]! - recipe.tint * .11
        color[2] = color[2]! + recipe.tint * .07
        const adjustedLuminance = color[0]! * .2126 + color[1]! * .7152 + color[2]! * .0722
        const saturation = 1 + recipe.saturation + recipe.vibrance * (1 - Math.min(1, Math.max(...color) - Math.min(...color)))
        color = color.map((channel) => adjustedLuminance + (channel - adjustedLuminance) * saturation) as [number, number, number]
        color = color.map((channel) => channel * (1 - recipe.fade) + recipe.fade * .025) as [number, number, number]
        const original = srgb
        const skinSignal = Math.max(0, original[0]! - original[1]!) * Math.max(0, original[1]! - original[2]!)
        const skinProtection = smoothstep(.002, .03, skinSignal) * (1 - smoothstep(.12, .32, skinSignal))
        color = color.map((channel, index) => {
          const protectedChannel = srgbToLinear(original[index]!)
          const amount = .25 * skinProtection
          return protectedChannel * amount + channel * (1 - amount)
        }) as [number, number, number]
        for (const channel of color) {
          data[offset] = Math.round(linearToSrgb(channel) * 255)
          offset += 1
        }
        data[offset] = 255
        offset += 1
      }
    }
  }
  return data
}

const lutCache = new Map<string, Uint8Array>()
const getLut = (look: StudioLook) => {
  let data = lutCache.get(look.id)
  if (!data) {
    data = makeLut(look.recipe)
    lutCache.set(look.id, data)
  }
  return data
}

const vertexShader = `#version 300 es
precision highp float;
out vec2 vUv;
void main() {
  vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  vUv = p;
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}`

const fragmentShader = `#version 300 es
precision highp float;
precision highp sampler3D;
in vec2 vUv;
out vec4 outColor;
uniform sampler2D uImage;
uniform sampler3D uLut;
uniform sampler2D uFaceMask;
uniform sampler2D uEyesMask;
uniform sampler2D uLipsMask;
uniform sampler2D uUnderEyesMask;
uniform sampler2D uTeethMask;
uniform sampler2D uBlemishesMask;
uniform float uIntensity;
uniform vec2 uResolution;
uniform float uExposure;
uniform float uContrast;
uniform float uSaturation;
uniform float uTemperature;
uniform float uTint;
uniform float uHighlights;
uniform float uShadows;
uniform float uFade;
uniform float uGrain;
uniform float uGrainSize;
uniform float uVignette;
uniform float uVignetteFeather;
uniform float uLookGrain;
uniform float uLookVignette;
uniform float uSharpness;
uniform float uSkinSmooth;
uniform float uSkinTexture;
uniform float uSkinTone;
uniform float uSkinRedness;
uniform float uSkinVitality;
uniform float uBlemish;
uniform float uUnderEye;
uniform float uEyeBrightness;
uniform float uEyeWhitening;
uniform float uEyeClarity;
uniform float uEyeVitality;
uniform float uTeethWhitening;
uniform float uLipVitality;
uniform float uLipBrightness;
uniform float uFaceLight;
uniform float uFaceShadows;
uniform float uFaceHighlights;
uniform float uFaceTemperature;
uniform float uShineReduction;
uniform float uGlow;
uniform float uGlowSpread;
uniform int uWeatherType;
uniform float uWeatherIntensity;
vec3 toLinear(vec3 c) {
  bvec3 low = lessThanEqual(c, vec3(0.04045));
  vec3 a = c / 12.92;
  vec3 b = pow((c + 0.055) / 1.055, vec3(2.4));
  return mix(b, a, low);
}
vec3 toSrgb(vec3 c) {
  c = max(c, vec3(0.0));
  bvec3 low = lessThanEqual(c, vec3(0.0031308));
  vec3 a = c * 12.92;
  vec3 b = 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055;
  return clamp(mix(b, a, low), 0.0, 1.0);
}
float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

// --- Procedural Weather FX Functions ---
vec3 applyRain(vec3 col, vec2 uv, float intensity, vec2 res) {
  if (intensity <= 0.001) return col;
  vec2 rainCoord = vec2(uv.x * 0.96 + uv.y * 0.22, uv.y);
  vec2 st1 = vec2(rainCoord.x * res.x * 0.035, rainCoord.y * 14.0);
  vec2 id1 = floor(st1);
  vec2 f1 = fract(st1);
  float h1 = hash(id1);
  float streak1 = 0.0;
  if (h1 > 0.68) {
    float xDist = abs(f1.x - 0.5);
    float yGrad = smoothstep(0.0, 0.4, f1.y) * (1.0 - smoothstep(0.6, 1.0, f1.y));
    streak1 = smoothstep(0.18, 0.02, xDist) * yGrad * (h1 - 0.68) * 3.12;
  }
  vec2 st2 = vec2(rainCoord.x * res.x * 0.08 + 31.4, rainCoord.y * 28.0 + 17.8);
  vec2 id2 = floor(st2);
  vec2 f2 = fract(st2);
  float h2 = hash(id2);
  float streak2 = 0.0;
  if (h2 > 0.72) {
    float xDist2 = abs(f2.x - 0.5);
    float yGrad2 = smoothstep(0.0, 0.3, f2.y) * (1.0 - smoothstep(0.7, 1.0, f2.y));
    streak2 = smoothstep(0.15, 0.02, xDist2) * yGrad2 * (h2 - 0.72) * 3.57;
  }
  float totalRain = clamp(streak1 * 0.85 + streak2 * 0.55, 0.0, 1.0) * intensity;
  vec3 rainColor = vec3(0.85, 0.92, 1.0);
  col = mix(col, rainColor, totalRain * 0.65);
  col += rainColor * totalRain * 0.35;
  return col;
}

vec3 applySnow(vec3 col, vec2 uv, float intensity, vec2 res) {
  if (intensity <= 0.001) return col;
  float totalSnow = 0.0;
  vec2 st1 = vec2(uv.x * 12.0 + uv.y * 1.5, uv.y * 8.0);
  vec2 id1 = floor(st1);
  vec2 f1 = fract(st1);
  float h1 = hash(id1);
  if (h1 > 0.80) {
    vec2 center = vec2(hash(id1 + vec2(1.1, 1.7)), hash(id1 + vec2(2.3, 2.9)));
    float d = length(f1 - center);
    float flake = smoothstep(0.28, 0.05, d) * (h1 - 0.80) * 5.0;
    totalSnow += flake * 0.85;
  }
  vec2 st2 = vec2(uv.x * 28.0 - uv.y * 2.0 + 47.1, uv.y * 20.0 + 13.9);
  vec2 id2 = floor(st2);
  vec2 f2 = fract(st2);
  float h2 = hash(id2);
  if (h2 > 0.76) {
    vec2 center2 = vec2(hash(id2 + vec2(3.7, 4.3)), hash(id2 + vec2(5.1, 5.7)));
    float d2 = length(f2 - center2);
    float flake2 = smoothstep(0.15, 0.02, d2) * (h2 - 0.76) * 4.16;
    totalSnow += flake2 * 0.7;
  }
  vec2 st3 = vec2(uv.x * 65.0 + uv.y * 4.0 + 89.2, uv.y * 45.0 + 72.3);
  vec2 id3 = floor(st3);
  vec2 f3 = fract(st3);
  float h3 = hash(id3);
  if (h3 > 0.86) {
    float d3 = length(f3 - vec2(0.5));
    float flake3 = smoothstep(0.12, 0.01, d3) * 0.4;
    totalSnow += flake3;
  }
  totalSnow = clamp(totalSnow, 0.0, 1.0) * intensity;
  vec3 snowColor = vec3(0.96, 0.98, 1.0);
  col = mix(col, snowColor, totalSnow * 0.90);
  col += snowColor * totalSnow * 0.25;
  return col;
}

vec3 applyStars(vec3 col, vec2 uv, float intensity) {
  if (intensity <= 0.001) return col;
  float lum = dot(col, vec3(0.2126, 0.7152, 0.0722));
  float skyConfidence = (1.0 - smoothstep(0.15, 0.75, lum)) * smoothstep(0.15, 0.65, uv.y);
  if (skyConfidence < 0.01) return col;
  vec2 st = uv * 140.0;
  vec2 id = floor(st);
  vec2 f = fract(st);
  float h = hash(id);
  float star = 0.0;
  vec3 starColor = vec3(1.0);
  if (h > 0.93) {
    vec2 pos = vec2(hash(id + vec2(0.1, 0.3)), hash(id + vec2(0.4, 0.7)));
    vec2 diff = f - pos;
    float dist = length(diff);
    star = smoothstep(0.08, 0.01, dist) * (h - 0.93) * 14.0;
    if (h > 0.985) {
      float crossTwinkle = max(0.0, 1.0 - abs(diff.x) * 18.0) * max(0.0, 1.0 - abs(diff.y) * 18.0);
      star += crossTwinkle * 1.5;
      starColor = (hash(id + vec2(0.9, 0.2)) > 0.5) ? vec3(0.85, 0.92, 1.0) : vec3(1.0, 0.95, 0.85);
    }
  }
  float nebula = (sin(uv.x * 3.5 + uv.y * 2.8) * sin(uv.x * 7.2 - uv.y * 4.1)) * 0.5 + 0.5;
  vec3 nebulaColor = vec3(0.25, 0.35, 0.65) * pow(nebula, 3.0) * 0.45;
  float starAmt = star * intensity * skyConfidence;
  col += starColor * starAmt + nebulaColor * skyConfidence * intensity * 0.4;
  return col;
}

vec3 applyFog(vec3 col, vec2 uv, float intensity) {
  if (intensity <= 0.001) return col;
  float n1 = sin(uv.x * 5.0 + uv.y * 3.0) * 0.5 + 0.5;
  float n2 = sin(uv.x * 11.0 - uv.y * 7.0 + 2.1) * 0.5 + 0.5;
  float n3 = sin(uv.x * 23.0 + uv.y * 14.0 + 4.8) * 0.5 + 0.5;
  float mist = (n1 * 0.5 + n2 * 0.32 + n3 * 0.18);
  float heightProfile = smoothstep(0.0, 0.35, uv.y) * (1.0 - smoothstep(0.65, 1.0, uv.y * 0.85));
  float fogMask = clamp(mist * heightProfile * 1.35, 0.0, 1.0) * intensity;
  vec3 fogColor = vec3(0.88, 0.90, 0.93);
  col = mix(col, fogColor, fogMask * 0.62);
  return col;
}

vec3 applyLightning(vec3 col, vec2 uv, float intensity) {
  if (intensity <= 0.001) return col;
  float boltY = 1.0 - uv.y;
  float zigzag = sin(boltY * 22.0) * 0.05 + sin(boltY * 55.0) * 0.02 + sin(boltY * 110.0) * 0.008;
  float boltX = 0.58 + zigzag;
  float distToBolt = abs(uv.x - boltX);
  float boltCore = exp(-distToBolt * 380.0) * smoothstep(0.95, 0.25, boltY);
  float boltGlow = exp(-distToBolt * 28.0) * smoothstep(0.95, 0.15, boltY);
  float branchY = clamp((boltY - 0.25) * 1.8, 0.0, 1.0);
  float branchZigzag = sin(branchY * 35.0) * 0.04 + sin(branchY * 70.0) * 0.015;
  float branchX = boltX + branchY * 0.22 + branchZigzag;
  float branchDist = abs(uv.x - branchX);
  float branchCore = exp(-branchDist * 420.0) * smoothstep(0.0, 0.2, branchY) * (1.0 - smoothstep(0.6, 1.0, branchY));
  float skyFlash = smoothstep(0.35, 1.0, uv.y) * 0.35;
  vec3 boltColor = vec3(0.75, 0.88, 1.0) * boltGlow * 1.8 + vec3(1.0) * (boltCore * 2.2 + branchCore * 1.5);
  vec3 flashColor = vec3(0.55, 0.70, 1.0) * skyFlash;
  col += (boltColor + flashColor) * intensity;
  return col;
}

vec3 applySunRays(vec3 col, vec2 uv, float intensity) {
  if (intensity <= 0.001) return col;
  vec2 sunPos = vec2(0.12, 0.98);
  vec2 rayDir = uv - sunPos;
  float dist = length(rayDir);
  float angle = atan(rayDir.y, rayDir.x);
  float shafts = pow(sin(angle * 14.0) * 0.5 + 0.5, 4.0) * 0.65
               + pow(sin(angle * 26.0 + 1.2) * 0.5 + 0.5, 3.0) * 0.35;
  float falloff = smoothstep(1.6, 0.05, dist);
  float rays = shafts * falloff * intensity;
  vec3 sunGold = vec3(1.0, 0.82, 0.52);
  col += sunGold * rays * 0.75;
  return col;
}

vec3 applyAurora(vec3 col, vec2 uv, float intensity) {
  if (intensity <= 0.001) return col;
  float sky = smoothstep(0.35, 0.85, uv.y);
  if (sky < 0.01) return col;
  float wave1 = sin(uv.x * 6.0 + sin(uv.y * 8.0) * 1.5) * 0.5 + 0.5;
  float wave2 = sin(uv.x * 12.0 - sin(uv.y * 12.0) * 2.0 + 1.8) * 0.5 + 0.5;
  float auroraCurtain = pow(wave1 * 0.7 + wave2 * 0.3, 2.5) * sky;
  vec3 green = vec3(0.20, 0.95, 0.50);
  vec3 violet = vec3(0.75, 0.25, 0.85);
  vec3 auroraColor = mix(green, violet, smoothstep(0.55, 0.95, uv.y));
  col += auroraColor * auroraCurtain * intensity * 0.85;
  return col;
}
void main() {
  vec4 source = texture(uImage, vUv);
  vec3 originalLinear = toLinear(source.rgb);

  // Anatomical and feature masks
  float faceRegion = texture(uFaceMask, vUv).a;
  vec4 eyeSample = texture(uEyesMask, vUv);
  float irisRegion = eyeSample.r;
  float scleraRegion = eyeSample.g;
  float eyeRegion = eyeSample.a;
  float lipRegion = texture(uLipsMask, vUv).a;
  float underEyeRegion = texture(uUnderEyesMask, vUv).a;
  float teethRegion = texture(uTeethMask, vUv).a;
  float touchedBlemishRegion = texture(uBlemishesMask, vUv).a;

  // High-accuracy skin tone confidence: human skin across all ethnicities has R > B and R >= G * 0.88
  float skinHueConfidence = smoothstep(0.01, 0.06, source.r - source.b)
    * smoothstep(-0.04, 0.04, source.r - source.g * 0.88);
  float featureExclusion = max(eyeRegion, max(lipRegion, teethRegion));
  float skinRegion = faceRegion * skinHueConfidence * (1.0 - featureExclusion);
  float faceToneRegion = faceRegion * (1.0 - featureExclusion * 0.85);

  vec3 lookSrgb = texture(uLut, clamp(source.rgb, 0.0, 1.0)).rgb;
  vec3 color = mix(originalLinear, toLinear(lookSrgb), uIntensity);
  vec2 texel = 1.0 / uResolution;

  // 1. Skin Smoothing & Texture (Pürüzsüzlük & Doku) - strictly preserves facial features & contours
  if ((uSkinSmooth > 0.001 || abs(uSkinTexture) > 0.001) && skinRegion > 0.001) {
    float smoothIntensity = clamp(uSkinSmooth, 0.0, 1.0);
    float filterRadius = smoothIntensity * 6.0 + 2.5;
    vec3 localSum = color;
    float localWeight = 1.0;
    const vec2 taps[12] = vec2[12](
      vec2( 0.0,  1.0), vec2( 0.0, -1.0), vec2( 1.0,  0.0), vec2(-1.0,  0.0),
      vec2( 0.7,  0.7), vec2(-0.7,  0.7), vec2( 0.7, -0.7), vec2(-0.7, -0.7),
      vec2( 0.0,  2.0), vec2( 0.0, -2.0), vec2( 2.0,  0.0), vec2(-2.0,  0.0)
    );
    for (int i = 0; i < 12; i++) {
      vec2 offset = taps[i] * texel * filterRadius;
      vec3 tap = toLinear(texture(uImage, clamp(vUv + offset, 0.0, 1.0)).rgb);
      vec3 diff = tap - originalLinear;
      // Strict edge stopping: stops smoothing at nose wings, nostrils, chin line, philtrum, and deep lines
      float rw = exp(-dot(diff, diff) * 85.0);
      localSum += tap * rw;
      localWeight += rw;
    }
    vec3 skinBase = localSum / max(localWeight, 0.001);
    vec3 highFreq = color - skinBase;

    // Smooth subtle color variations naturally without plastic blurring
    float smoothBlend = smoothIntensity * skinRegion * 0.72;
    vec3 smoothedSkin = mix(color, skinBase, smoothBlend);

    // Micro-texture restoration (-100 to +100)
    smoothedSkin += highFreq * (uSkinTexture * 0.85 * skinRegion);
    color = mix(color, smoothedSkin, skinRegion);
  }

  // 2. Blemish Removal (Leke Azaltma)
  if ((uBlemish > 0.001 || touchedBlemishRegion > 0.001) && skinRegion > 0.001) {
    float nearbyLuma = dot(
      toLinear(texture(uImage, vUv - texel * 5.0).rgb) +
      toLinear(texture(uImage, vUv + texel * 5.0).rgb) +
      toLinear(texture(uImage, vUv - vec2(0.0, texel.y * 5.0)).rgb) +
      toLinear(texture(uImage, vUv + vec2(0.0, texel.y * 5.0)).rgb),
      vec3(.2126, .7152, .0722)
    ) * 0.25;
    float localLuma = dot(originalLinear, vec3(.2126, .7152, .0722));
    float spotContrast = clamp(abs(localLuma - nearbyLuma) * 4.2, 0.0, 1.0);
    float autoBlemishMask = spotContrast * clamp(uBlemish, 0.0, 1.0) * skinRegion;
    float manualBlemishMask = touchedBlemishRegion * faceRegion * 0.95;
    float blemishAmount = max(autoBlemishMask, manualBlemishMask);
    if (blemishAmount > 0.001) {
      vec3 cleanAvg = (
        toLinear(texture(uImage, vUv - texel * 3.5).rgb) +
        toLinear(texture(uImage, vUv + texel * 3.5).rgb) +
        toLinear(texture(uImage, vUv - vec2(0.0, texel.y * 3.5)).rgb) +
        toLinear(texture(uImage, vUv + vec2(0.0, texel.y * 3.5)).rgb)
      ) * 0.25;
      color = mix(color, cleanAvg, blemishAmount * 0.88);
    }
  }

  // Global adjustments
  color *= exp2(uExposure);
  float lum = dot(color, vec3(0.2126, 0.7152, 0.0722));
  float shadowMask = 1.0 - smoothstep(0.03, 0.65, lum);
  float highlightMask = smoothstep(0.35, 0.98, lum);
  color += vec3(uShadows * 0.35 * shadowMask + uHighlights * 0.25 * highlightMask);
  float pivot = 0.18;
  color = (color - pivot) * (1.0 + uContrast) + pivot;
  color.r += uTemperature * 0.12 + uTint * 0.07;
  color.g -= uTint * 0.11;
  color.b -= uTemperature * 0.12 - uTint * 0.07;
  float adjustedLum = dot(color, vec3(0.2126, 0.7152, 0.0722));
  color = mix(vec3(adjustedLum), color, max(0.0, 1.0 + uSaturation));
  color = color * (1.0 - uFade) + vec3(0.025 * uFade);

  // 3. Face Relighting (Yüz Işığı - Strictly multiplicative photographic exposure)
  if (faceToneRegion > 0.001) {
    // Aydınlat (Face Exposure)
    if (abs(uFaceLight) > 0.001) {
      float lightFactor = 1.0 + uFaceLight * 0.45;
      color = mix(color, color * lightFactor, faceToneRegion);
    }
    // Gölgeler (Shadow Fill Light - Lifts dark shadows without washing out blacks)
    if (abs(uFaceShadows) > 0.001) {
      float curLum = dot(color, vec3(0.2126, 0.7152, 0.0722));
      float shadowWeight = pow(1.0 - smoothstep(0.04, 0.68, curLum), 1.4);
      float shadowLift = 1.0 + uFaceShadows * shadowWeight * 0.60;
      color = mix(color, color * shadowLift, faceToneRegion);
    }
    // Parlak Alanlar (Highlights)
    if (abs(uFaceHighlights) > 0.001) {
      float curLum = dot(color, vec3(0.2126, 0.7152, 0.0722));
      float highlightWeight = smoothstep(0.42, 0.95, curLum);
      float highlightScale = 1.0 + uFaceHighlights * highlightWeight * 0.40;
      color = mix(color, color * highlightScale, faceToneRegion);
    }
    // Sıcaklık (Face Temperature - Golden sunlight vs porcelain cool)
    if (abs(uFaceTemperature) > 0.001) {
      vec3 warmColor = color;
      warmColor.r *= 1.0 + uFaceTemperature * 0.18;
      warmColor.b *= max(0.0, 1.0 - uFaceTemperature * 0.18);
      color = mix(color, warmColor, faceToneRegion);
    }
    // Parlama Azaltma (Shine Reduction - Mattifies oily specular glare)
    if (uShineReduction > 0.001) {
      float curLum = dot(color, vec3(0.2126, 0.7152, 0.0722));
      float glare = smoothstep(0.55, 0.92, curLum) * skinRegion;
      color = mix(color, color * (1.0 - glare * uShineReduction * 0.35), faceToneRegion);
    }
  }

  // 4. Skin Corrections (Kızarıklık, Ton Eşitle, Canlılık)
  if (skinRegion > 0.001) {
    // Kızarıklık (Redness Reduction) - Harmonizes flush into natural warm golden-peach skin; NEVER green!
    if (uSkinRedness > 0.001) {
      float healthyGreenTarget = color.r * 0.76;
      float rednessExcess = max(0.0, healthyGreenTarget - color.g);
      float flushMask = smoothstep(0.015, 0.18, rednessExcess) * skinRegion;
      float redCorrect = clamp(uSkinRedness, 0.0, 1.0) * flushMask;
      color.g += rednessExcess * redCorrect * 0.65;
      color.r -= rednessExcess * redCorrect * 0.30;
    }

    // Ton Eşitle (Skin Tone Harmonization)
    if (uSkinTone > 0.001) {
      float skinLuma = dot(color, vec3(0.2126, 0.7152, 0.0722));
      vec3 harmoniousTone = vec3(skinLuma * 1.08, skinLuma * 0.85, skinLuma * 0.68);
      color = mix(color, mix(color, harmoniousTone, 0.28), clamp(uSkinTone, 0.0, 1.0) * skinRegion * 0.65);
    }

    // Canlılık (Skin Vitality)
    if (uSkinVitality > 0.001) {
      float skinLuma = dot(color, vec3(0.2126, 0.7152, 0.0722));
      float vitAmount = clamp(uSkinVitality, 0.0, 1.0) * skinRegion;
      vec3 vibrantSkin = skinLuma + (color - skinLuma) * (1.0 + vitAmount * 0.65);
      vibrantSkin.r *= 1.0 + vitAmount * 0.08;
      vibrantSkin.g *= 1.0 + vitAmount * 0.03;
      color = mix(color, vibrantSkin, vitAmount);
    }
  }

  // 5. Under-Eye Dark Circles (Göz Altı - Tear trough lift & warm concealer)
  float underEyeArea = underEyeRegion * (1.0 - eyeRegion * 0.95);
  if (underEyeArea > 0.001 && uUnderEye > 0.001) {
    float liftAmount = clamp(uUnderEye, 0.0, 1.0) * underEyeArea;
    float curLuma = dot(color, vec3(0.2126, 0.7152, 0.0722));
    float shadowDeficit = 1.0 - smoothstep(0.08, 0.65, curLuma);
    float exposureLift = 1.0 + liftAmount * shadowDeficit * 0.44;
    color *= exposureLift;

    float liftedLuma = dot(color, vec3(0.2126, 0.7152, 0.0722));
    vec3 concealerTone = vec3(liftedLuma * 1.09, liftedLuma * 0.88, liftedLuma * 0.72);
    color = mix(color, concealerTone, liftAmount * shadowDeficit * 0.35);
  }

  // 6. Eyes (Göz: Canlılık, Beyazlık, Parlaklık, Netlik - Anatomically Isolated Iris & Sclera)
  if (eyeRegion > 0.001) {
    // A. Göz Beyazlığı (Sclera Whitening - Strictly applies ONLY to scleraRegion!)
    if (uEyeWhitening > 0.001 && scleraRegion > 0.001) {
      float whiteAmount = clamp(uEyeWhitening, 0.0, 1.0) * scleraRegion;
      float redExcess = max(0.0, color.r - max(color.g, color.b) * 1.03);
      color.r -= redExcess * whiteAmount * 0.90;
      float yellowExcess = max(0.0, (color.r + color.g) * 0.5 - color.b);
      color.b += yellowExcess * whiteAmount * 0.55;
      color *= 1.0 + whiteAmount * 0.36;
      float cleanLuma = dot(color, vec3(0.2126, 0.7152, 0.0722));
      color = mix(color, vec3(cleanLuma * 1.02), whiteAmount * 0.30);
    }

    // B. Göz Canlılığı (Iris Vitality - Strictly applies ONLY to irisRegion; never touches sclera!)
    if (uEyeVitality > 0.001 && irisRegion > 0.001) {
      float vitAmount = clamp(uEyeVitality, 0.0, 1.0) * irisRegion;
      float irisLuma = dot(color, vec3(0.2126, 0.7152, 0.0722));
      vec3 vibrantIris = irisLuma + (color - irisLuma) * (1.0 + vitAmount * 1.35);
      vibrantIris.r *= 1.0 + vitAmount * 0.16;
      vibrantIris.g *= 1.0 + vitAmount * 0.09;
      float irisPivot = 0.14;
      vibrantIris = (vibrantIris - irisPivot) * (1.0 + vitAmount * 0.25) + irisPivot;
      color = mix(color, max(vec3(0.0), vibrantIris), vitAmount);
    }

    // C. Göz Parlaklığı (Eye Radiance / Brightness - Lights up the entire eye and makes it sparkle)
    if (uEyeBrightness > 0.001) {
      float brightAmount = clamp(uEyeBrightness, 0.0, 1.0) * eyeRegion;
      float eyeLuma = dot(color, vec3(0.2126, 0.7152, 0.0722));
      // 1. Overall eye exposure lift (makes eyes look awake, clear, and bright)
      float eyeLift = 1.0 + brightAmount * 0.68;
      color *= eyeLift;
      // 2. Specular catchlight sparkle (corneal reflections)
      float catchlight = smoothstep(0.12, 0.75, eyeLuma);
      color *= 1.0 + brightAmount * catchlight * 0.55;
      // 3. Iris crystalline gleam
      float irisGleam = smoothstep(0.04, 0.45, eyeLuma);
      color *= 1.0 + brightAmount * irisGleam * 0.35;
      // 4. Pupil protection: preserve deepest black center so pupil stays dark
      float pupilMask = 1.0 - smoothstep(0.015, 0.065, eyeLuma);
      color = mix(color, originalLinear, pupilMask * 0.80);
    }

    // D. Göz Netliği (Eye Clarity)
    if (uEyeClarity > 0.001) {
      float clarityAmount = clamp(uEyeClarity, 0.0, 1.0) * eyeRegion;
      float eyePivot = 0.16;
      color = (color - eyePivot) * (1.0 + clarityAmount * 0.35) + eyePivot;
      color = max(vec3(0.0), color);
    }
  }

  // 7. Lips (Dudak: Canlılık & Parlaklık - Covers BOTH Upper and Lower Lip!)
  if (lipRegion > 0.001) {
    float lipLuma = dot(color, vec3(0.2126, 0.7152, 0.0722));

    // Dudak Canlılığı (Rosy Capillary Circulation - Preserves texture, NOT opaque lipstick)
    if (uLipVitality > 0.001) {
      float vitalityAmount = clamp(uLipVitality, 0.0, 1.0) * lipRegion;
      vec3 vibrantLip = lipLuma + (color - lipLuma) * (1.0 + vitalityAmount * 0.95);
      vibrantLip.r *= 1.0 + vitalityAmount * 0.15;
      vibrantLip.g *= 1.0 + vitalityAmount * 0.04;
      color = mix(color, vibrantLip, vitalityAmount * 0.80);
    }

    // Dudak Parlaklığı (Dewy Lip Gloss - Visible, juicy, plumping shine on BOTH upper and lower lip)
    if (uLipBrightness > 0.001) {
      float brightAmount = clamp(uLipBrightness, 0.0, 1.0) * lipRegion;
      // Ambient gloss foundation: illuminates both upper and lower lip tissue with clear glass oil
      float glossAmbient = 0.42;
      // Specular shine on the natural curves and moisture of both lips
      float glossSpecular = smoothstep(0.06, 0.65, lipLuma) * 0.65;
      float glossFactor = 1.0 + brightAmount * (glossAmbient + glossSpecular);
      color *= glossFactor;
    }
  }

  // 8. Teeth (Diş: Beyazlat)
  if (teethRegion > 0.001 && uTeethWhitening > 0.001) {
    float teethLuma = dot(color, vec3(0.2126, 0.7152, 0.0722));
    float teethZone = smoothstep(0.06, 0.35, teethLuma);
    float teethAmount = clamp(uTeethWhitening, 0.0, 1.0) * teethRegion * teethZone;
    float yellowDeficit = max(0.0, (color.r + color.g) * 0.5 - color.b);
    color.b += yellowDeficit * teethAmount * 0.70;
    color.r -= max(0.0, color.r - color.g) * teethAmount * 0.40;
    color *= 1.0 + teethAmount * 0.38;
    float cleanedTeethLuma = dot(color, vec3(0.2126, 0.7152, 0.0722));
    color = mix(color, vec3(cleanedTeethLuma * 1.04), teethAmount * 0.35);
  }

  // 9. Glow (Işıltı & Yayılım - Ethereal highlight diffusion bloom)
  if (uGlow > 0.001) {
    float glowSpreadPixels = 3.0 + uGlowSpread * 18.0;
    vec2 glowOffset = texel * glowSpreadPixels;
    vec3 glowSample = (
      texture(uImage, vUv + vec2(glowOffset.x, 0.0)).rgb +
      texture(uImage, vUv - vec2(glowOffset.x, 0.0)).rgb +
      texture(uImage, vUv + vec2(0.0, glowOffset.y)).rgb +
      texture(uImage, vUv - vec2(0.0, glowOffset.y)).rgb +
      texture(uImage, vUv + glowOffset * 0.707).rgb +
      texture(uImage, vUv - glowOffset * 0.707).rgb
    ) / 6.0;
    vec3 glowLinear = toLinear(glowSample);
    float sampleLum = dot(glowLinear, vec3(0.2126, 0.7152, 0.0722));
    float highlightBloom = smoothstep(0.32, 0.85, sampleLum);
    vec3 bloomColor = glowLinear * (1.0 + vec3(0.08, 0.04, -0.02));
    float glowAmount = clamp(uGlow, 0.0, 1.0) * 0.45;
    color += bloomColor * highlightBloom * glowAmount;
  }

  // 10. Procedural Weather FX (Rain streaks, Snowflakes, Night Stars, Rolling Fog, Lightning, God Rays, Aurora)
  if (uWeatherType == 1) {
    color = applyRain(color, vUv, uWeatherIntensity, uResolution);
  } else if (uWeatherType == 2) {
    color = applySnow(color, vUv, uWeatherIntensity, uResolution);
  } else if (uWeatherType == 3) {
    color = applyStars(color, vUv, uWeatherIntensity);
  } else if (uWeatherType == 4) {
    color = applyFog(color, vUv, uWeatherIntensity);
  } else if (uWeatherType == 5) {
    color = applyLightning(color, vUv, uWeatherIntensity);
  } else if (uWeatherType == 6) {
    color = applySunRays(color, vUv, uWeatherIntensity);
  } else if (uWeatherType == 7) {
    color = applyAurora(color, vUv, uWeatherIntensity);
  }

  // Vignette, grain & sharpness
  // Frame-concentric photographic vignette: darkens symmetrically towards all edges and corners
  vec2 vignetteCoord = (vUv - 0.5) * 2.0;
  float vignetteRadius = length(vignetteCoord);
  float vignetteFeather = clamp(uVignetteFeather, 0.05, 1.0);
  float vignetteInner = mix(0.70, 0.15, vignetteFeather);
  float vignetteOuter = mix(1.40, 1.05, vignetteFeather);
  float vignette = smoothstep(vignetteInner, vignetteOuter, vignetteRadius);
  float vignetteAmount = clamp(uVignette * 1.35 + uLookVignette * uIntensity, 0.0, 1.0);
  color *= 1.0 - vignette * vignetteAmount;
  color = toSrgb(color);
  float grainAmount = clamp(uGrain + uLookGrain * uIntensity, 0.0, 0.45);
  vec2 grainCoord = floor(gl_FragCoord.xy / max(0.35, uGrainSize));
  float noise = hash(grainCoord + vec2(0.13, 0.71)) - 0.5;
  color += vec3(noise * grainAmount * 0.24);
  if (uSharpness > 0.0) {
    vec2 texel = 1.0 / uResolution;
    vec3 neighbors = texture(uImage, vUv - vec2(texel.x, 0.0)).rgb
      + texture(uImage, vUv + vec2(texel.x, 0.0)).rgb
      + texture(uImage, vUv - vec2(0.0, texel.y)).rgb
      + texture(uImage, vUv + vec2(0.0, texel.y)).rgb;
    color += (toSrgb(originalLinear) - neighbors * 0.25) * uSharpness;
  }
  outColor = vec4(clamp(color, 0.0, 1.0), source.a);
}`

const compileShader = (gl: WebGL2RenderingContext, type: number, source: string) => {
  const shader = gl.createShader(type)
  if (!shader) throw new Error('Görsel filtre başlatılamadı.')
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const message = gl.getShaderInfoLog(shader) || 'Görsel filtre derlenemedi.'
    gl.deleteShader(shader)
    throw new Error(message)
  }
  return shader
}

const WEATHER_TYPE_MAP: Record<WeatherEffectType, number> = {
  none: 0,
  rain: 1,
  snow: 2,
  stars: 3,
  fog: 4,
  lightning: 5,
  sunrays: 6,
  aurora: 7,
}

class StudioLutRenderer {
  private readonly gl: WebGL2RenderingContext
  private readonly program: WebGLProgram
  private readonly imageTexture: WebGLTexture
  private readonly lutTexture: WebGLTexture
  private readonly beautyMaskTextures: Record<keyof StudioBeautyMasks, WebGLTexture>
  private currentLutId = ''
  private currentImage: TexImageSource | null = null
  private readonly currentMaskSources: Partial<Record<keyof StudioBeautyMasks, HTMLCanvasElement | null>> = {}
  private readonly uniforms: Record<string, WebGLUniformLocation | null> = {}

  constructor(private readonly canvas: HTMLCanvasElement) {
    const gl = canvas.getContext('webgl2', { alpha: false, antialias: false, preserveDrawingBuffer: true, premultipliedAlpha: false })
    if (!gl) throw new Error('Bu cihazda donanım hızlandırmalı filtre kullanılamıyor.')
    this.gl = gl
    const program = gl.createProgram()
    if (!program) throw new Error('Görsel filtre başlatılamadı.')
    gl.attachShader(program, compileShader(gl, gl.VERTEX_SHADER, vertexShader))
    gl.attachShader(program, compileShader(gl, gl.FRAGMENT_SHADER, fragmentShader))
    gl.linkProgram(program)
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program) || 'Görsel filtre bağlanamadı.')
    this.program = program
    const imageTexture = gl.createTexture()
    const lutTexture = gl.createTexture()
    if (!imageTexture || !lutTexture) throw new Error('Görsel filtre belleği ayrılamadı.')
    this.imageTexture = imageTexture
    this.lutTexture = lutTexture
    const beautyMaskTextures = {} as Record<keyof StudioBeautyMasks, WebGLTexture>
    for (const key of ['face', 'eyes', 'lips', 'underEyes', 'teeth', 'blemishes'] as const) {
      const texture = gl.createTexture()
      if (!texture) throw new Error('Yüz rötuşu başlatılamadı.')
      beautyMaskTextures[key] = texture
    }
    this.beautyMaskTextures = beautyMaskTextures
    gl.useProgram(program)
    for (const name of ['uImage', 'uLut', 'uFaceMask', 'uEyesMask', 'uLipsMask', 'uUnderEyesMask', 'uTeethMask', 'uBlemishesMask', 'uIntensity', 'uResolution', 'uExposure', 'uContrast', 'uSaturation', 'uTemperature', 'uTint', 'uHighlights', 'uShadows', 'uFade', 'uGrain', 'uGrainSize', 'uVignette', 'uVignetteFeather', 'uLookGrain', 'uLookVignette', 'uSharpness', 'uSkinSmooth', 'uSkinTexture', 'uSkinTone', 'uSkinRedness', 'uSkinVitality', 'uBlemish', 'uUnderEye', 'uEyeBrightness', 'uEyeWhitening', 'uEyeClarity', 'uEyeVitality', 'uTeethWhitening', 'uLipVitality', 'uLipBrightness', 'uFaceLight', 'uFaceShadows', 'uFaceHighlights', 'uFaceTemperature', 'uShineReduction', 'uGlow', 'uGlowSpread', 'uWeatherType', 'uWeatherIntensity']) {
      this.uniforms[name] = gl.getUniformLocation(program, name)
    }
    gl.uniform1i(this.uniforms.uImage ?? null, 0)
    gl.uniform1i(this.uniforms.uLut ?? null, 1)
    const maskUniforms = { face: 'uFaceMask', eyes: 'uEyesMask', lips: 'uLipsMask', underEyes: 'uUnderEyesMask', teeth: 'uTeethMask', blemishes: 'uBlemishesMask' } as const
    ;(Object.keys(maskUniforms) as Array<keyof StudioBeautyMasks>).forEach((key, index) => gl.uniform1i(this.uniforms[maskUniforms[key]] ?? null, index + 2))
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1)
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, 0)
  }

  render(image: TexImageSource, width: number, height: number, adjustments: StudioFilterAdjustments, selectedLook: StudioLook, masks?: StudioBeautyMasks) {
    const gl = this.gl
    if (this.canvas.width !== width) this.canvas.width = width
    if (this.canvas.height !== height) this.canvas.height = height
    gl.viewport(0, 0, width, height)
    gl.useProgram(this.program)

    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, this.imageTexture)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    if (this.currentImage !== image) {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image)
      this.currentImage = image
    }

    gl.activeTexture(gl.TEXTURE1)
    gl.bindTexture(gl.TEXTURE_3D, this.lutTexture)
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE)
    if (this.currentLutId !== selectedLook.id) {
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 0)
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, 0)
      gl.texImage3D(gl.TEXTURE_3D, 0, gl.RGBA8, LUT_SIZE, LUT_SIZE, LUT_SIZE, 0, gl.RGBA, gl.UNSIGNED_BYTE, getLut(selectedLook))
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1)
      const uploadError = gl.getError()
      if (uploadError !== gl.NO_ERROR) throw new Error(`Renk profili GPU'ya yüklenemedi (${uploadError}).`)
      this.currentLutId = selectedLook.id
    }

    const maskKeys = ['face', 'eyes', 'lips', 'underEyes', 'teeth', 'blemishes'] as const
    maskKeys.forEach((key, index) => {
      gl.activeTexture(gl.TEXTURE2 + index)
      gl.bindTexture(gl.TEXTURE_2D, this.beautyMaskTextures[key])
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
      const mask = masks?.[key]
      if (this.currentMaskSources[key] !== (mask ?? null)) {
        if (mask) gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, mask)
        else gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array(4))
        this.currentMaskSources[key] = mask ?? null
      }
    })

    const set1 = (name: string, value: number) => gl.uniform1f(this.uniforms[name] ?? null, value)
    gl.uniform2f(this.uniforms.uResolution ?? null, width, height)
    set1('uIntensity', Math.max(0, Math.min(100, adjustments.intensity)) / 100)
    set1('uExposure', adjustments.exposure)
    set1('uContrast', adjustments.contrast)
    set1('uSaturation', adjustments.saturation)
    set1('uTemperature', adjustments.temperature)
    set1('uTint', adjustments.tint)
    set1('uHighlights', adjustments.highlights)
    set1('uShadows', adjustments.shadows)
    set1('uFade', adjustments.fade)
    set1('uGrain', adjustments.grain)
    set1('uGrainSize', adjustments.grainSize)
    set1('uVignette', adjustments.vignette)
    set1('uVignetteFeather', adjustments.vignetteFeather)
    set1('uLookGrain', selectedLook.recipe.grain)
    set1('uLookVignette', selectedLook.recipe.vignette)
    set1('uSharpness', Math.max(0, Math.min(1, adjustments.sharpness)))
    const weatherType = selectedLook.recipe.weather ? (WEATHER_TYPE_MAP[selectedLook.recipe.weather] ?? 0) : 0
    const weatherIntensity = ((selectedLook.recipe.weatherIntensity ?? 1) * Math.max(0, Math.min(100, adjustments.intensity))) / 100
    gl.uniform1i(this.uniforms.uWeatherType ?? null, weatherType)
    set1('uWeatherIntensity', weatherIntensity)
    for (const [key, value] of Object.entries(adjustments.beauty)) {
      const uniform = `u${key[0]?.toUpperCase()}${key.slice(1)}`
      set1(uniform, value)
    }
    gl.drawArrays(gl.TRIANGLES, 0, 3)
    const drawError = gl.getError()
    if (drawError !== gl.NO_ERROR) throw new Error(`Görsel GPU'da işlenemedi (${drawError}).`)
    return this.canvas
  }
}

const rendererByCanvas = new WeakMap<HTMLCanvasElement, StudioLutRenderer>()
const getRenderer = (canvas: HTMLCanvasElement) => {
  let renderer = rendererByCanvas.get(canvas)
  if (!renderer) {
    renderer = new StudioLutRenderer(canvas)
    rendererByCanvas.set(canvas, renderer)
  }
  return renderer
}

export const renderStudioLook = (
  image: TexImageSource,
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
  adjustments: StudioFilterAdjustments,
  masks?: StudioBeautyMasks,
) => {
  const resolvedId = LOOK_ALIASES[adjustments.lookId] ?? adjustments.lookId
  const selectedLook = STUDIO_LOOKS.find((candidate) => candidate.id === resolvedId) || STUDIO_LOOKS[0]!
  return getRenderer(canvas).render(image, width, height, adjustments, selectedLook, masks)
}

export const getStudioLook = (id: string) => {
  const resolvedId = LOOK_ALIASES[id] ?? id
  return STUDIO_LOOKS.find((candidate) => candidate.id === resolvedId) || STUDIO_LOOKS[0]!
}
